import { createContext, useState, useContext, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import axios from "axios";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for SSO callback
    const params = new URLSearchParams(window.location.search);
    const ssoCode = params.get("sso_code");
    const ssoError = params.get("sso_error");

    if (ssoError) {
      console.error("SSO error:", ssoError);
      window.history.replaceState({}, "", "/login");
      setIsLoading(false);
      return;
    }

    if (ssoCode) {
      window.history.replaceState({}, "", "/");
      axios.post("/auth/sso/exchange", { code: ssoCode })
        .then((res) => {
          localStorage.setItem("accessToken", res.data.accessToken);
          localStorage.setItem("refreshToken", res.data.refreshToken);
          setUser({ email: res.data.user.email, role: res.data.user.role });
        })
        .catch(() => { setUser(null); })
        .finally(() => setIsLoading(false));
      return;
    }

    // Check for existing token
    const token = localStorage.getItem("accessToken");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 > Date.now()) {
          setUser({ email: decoded.email, role: decoded.role });
        } else {
          tryRefresh();
          return;
        }
      } catch {
        localStorage.clear();
      }
    }
    setIsLoading(false);
  }, []);

  async function tryRefresh() {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      localStorage.clear();
      setIsLoading(false);
      return;
    }
    try {
      const res = await axios.post("/auth/refresh", { refreshToken });
      localStorage.setItem("accessToken", res.data.accessToken);
      localStorage.setItem("refreshToken", res.data.refreshToken);
      setUser(res.data.user);
    } catch {
      localStorage.clear();
    }
    setIsLoading(false);
  }

  async function login(email, password) {
    const res = await axios.post("/auth/login", { email, password });
    localStorage.setItem("accessToken", res.data.accessToken);
    localStorage.setItem("refreshToken", res.data.refreshToken);
    setUser(res.data.user);
    return res.data;
  }

  async function changePassword(email, currentPassword, newPassword) {
    const res = await axios.post("/auth/change-password", { email, currentPassword, newPassword });
    localStorage.setItem("accessToken", res.data.accessToken);
    localStorage.setItem("refreshToken", res.data.refreshToken);
    setUser(res.data.user);
    return res.data;
  }

  async function logout() {
    const refreshToken = localStorage.getItem("refreshToken");
    const accessToken = localStorage.getItem("accessToken");
    try {
      await axios.post("/auth/logout", { refreshToken }, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    } catch {
      // Logout even if server call fails
    }
    localStorage.clear();
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
